//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material'
import React, { ReactElement, useEffect } from 'react'

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined'
import IconButton from '@mui/material/IconButton'
import Modal from '@mui/material/Modal'
import Paper from '@mui/material/Paper'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material'
import { DocumentInfo, listDocuments, listDocumentsLyt, urlForPdfFile, deletePdfLyt } from '@react/apx/AwsApx'
import { EthAddr } from '@shared/SchemaTypes'

import { useOtChainState } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { RoleEnum } from '@shared/Enums'

const modalStyleFullPage = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  borderRadius: '20px',
  boxShadow: '0px 16px 50px rgba(48, 49, 51, 0.08), 0px 0px 0.5px #303133',
  p: 4,
}

export default function ViewDocumentList(props: {
  poolAddr: EthAddr
  setHasRequiredDocs?: (value: boolean) => void
  refreshNumber: number
  isLytMode?: boolean
}): ReactElement {
  const [fileList, setFileList] = React.useState<DocumentInfo[]>([])
  const { poolAddr } = props
  const [openModal, setOpenModal] = React.useState(false)
  const [pdfViewName, setPdfViewName] = React.useState<string | undefined>(undefined)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [fileToDelete, setFileToDelete] = React.useState<string | null>(null)

  const {
    stageConfiguration: { stageName, restAPIKey },
    chainConfiguration: {
      chainId,

      perimeterAddresses,
    },
    userConnection: { roleEnum },
    cognitoUser,
  } = useOtChainState()

  const theme = useTheme()

  const loadDocuments = async (): Promise<void> => {
    if (poolAddr && stageName) {
      try {
        const fl = props.isLytMode
          ? await listDocumentsLyt(stageName, poolAddr)
          : await listDocuments(stageName, poolAddr)

        // Sort by newest first (most recent upload date)
        const sortedFiles = fl.sort((a, b) => {
          const dateA = new Date(a.modifiedDate)
          const dateB = new Date(b.modifiedDate)
          return dateB.getTime() - dateA.getTime()
        })

        if (sortedFiles.length > 0 && props.setHasRequiredDocs) {
          props.setHasRequiredDocs(true)
        } else if (props.setHasRequiredDocs) {
          props.setHasRequiredDocs(false)
        }
        setFileList(sortedFiles)
      } catch (e) {
        console.error(e)
      }
    }
  }

  useEffect(() => {
    loadDocuments()
  }, [props.refreshNumber, poolAddr, chainId, perimeterAddresses, props.isLytMode])

  const handlePDFOpen = (fileName: string): void => {
    logOT.info('handlePDFOpen', fileName)
    setPdfViewName(fileName)
    setOpenModal(true)
  }

  const handleDeleteClick = (fileName: string, event: React.MouseEvent): void => {
    event.stopPropagation()
    setFileToDelete(fileName)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async (): Promise<void> => {
    if (fileToDelete && poolAddr && cognitoUser && props.isLytMode) {
      try {
        const displayName = fileToDelete.substring(fileToDelete.lastIndexOf('/') + 1)
        await deletePdfLyt(stageName, poolAddr, displayName, restAPIKey, cognitoUser)
        await loadDocuments()
        setDeleteDialogOpen(false)
        setFileToDelete(null)
      } catch (error) {
        logOT.error('Error deleting file:', error)
        setDeleteDialogOpen(false)
        setFileToDelete(null)
      }
    }
  }

  const handleDeleteCancel = (): void => {
    setDeleteDialogOpen(false)
    setFileToDelete(null)
  }

  const canDelete =
    props.isLytMode &&
    (roleEnum === RoleEnum.POOL_ADMIN ||
      roleEnum === RoleEnum.BORROWER ||
      roleEnum === RoleEnum.ISSUER ||
      roleEnum === RoleEnum.PROTOCOL_MANAGER)

  const pdfFileName = pdfViewName?.split('/').pop() ?? ''
  return (
    <React.Fragment>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Paper sx={modalStyleFullPage}>
          <Typography align="left" sx={{ display: 'inline', float: 'left', fontWeight: 'bold' }} variant={'h4'}>
            {pdfFileName}{' '}
          </Typography>
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{
              display: 'inline',
              height: '14px',
              width: '14px',
              padding: 0,
              float: 'right',
              color: theme.palette.primary.main,
            }}
          >
            <CloseIcon
              sx={{
                width: '12px',
                height: '12px',
                marginTop: 0,
                color: theme.palette.fg.muted,
              }}
            />
          </IconButton>

          <Box sx={{ marginTop: '32px' }}>
            {pdfViewName && (
              <iframe style={{ height: 900, width: '100%' }} src={urlForPdfFile(stageName, pdfViewName)} />
            )}
          </Box>
        </Paper>
      </Modal>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ color: 'white', backgroundColor: '#1a1a1a' }}>Delete Document</DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1a1a1a' }}>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Are you sure you want to delete this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a1a1a' }}>
          <Button onClick={handleDeleteCancel} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleDeleteConfirm()}
            sx={{
              color: '#ff6b6b',
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 107, 0.1)',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <List>
        {fileList.map((fileInfo) => {
          return (
            <ListItem
              key={fileInfo.fileName}
              disablePadding
              onClick={() => {
                handlePDFOpen(fileInfo.fileName)
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    edge="end"
                    aria-label="open"
                    onClick={() => {
                      handlePDFOpen(fileInfo.fileName)
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                  {canDelete && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={(e) => handleDeleteClick(fileInfo.fileName, e)}
                      sx={{
                        color: '#000000',
                        '&:hover': {
                          color: '#ff6b6b',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              }
            >
              <ListItemButton>
                <ListItemIcon>
                  <TextSnippetOutlinedIcon
                    viewBox={'0 0 25,20'}
                    preserveAspectRatio="none"
                    sx={{
                      maxWidth: '20px',
                      height: '25px',
                      color: theme.palette.accent1.main,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={fileInfo.fileName.substring(fileInfo.fileName.lastIndexOf('/') + 1)}
                  secondary={new Date(fileInfo.modifiedDate).toLocaleDateString()}
                />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
    </React.Fragment>
  )
}
